import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, Tag as TagIcon, Search } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import TagsMenuBar from "@/components/TagsMenuBar";
import SiteFooter from "@/components/SiteFooter";
import { fetchAllTags } from "@/lib/api";

export default function TagsIndex() {
  const dealer = new URLSearchParams(useLocation().search).get("d") || "orevital";
  const dq = dealer && dealer !== "orevital" ? `?d=${dealer}` : "";
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    fetchAllTags()
      .then((t) => alive && setTags(t))
      .catch(() => alive && setTags([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? tags.filter((t) => t.tag_name.toLowerCase().includes(term)) : tags;
  }, [tags, q]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f7f4]" data-testid="tags-index-page">
      <PublicHeader nextPath={`/tags${dq}`} />
      <TagsMenuBar />

      <div className="bg-gradient-to-br from-teal-700 to-emerald-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight inline-flex items-center gap-2.5">
            <TagIcon size={28} /> Shop by Tag
          </h1>
          <p className="text-emerald-100 mt-1 text-sm" data-testid="tags-count">
            {loading ? "Loading…" : `${tags.length} tags available`}
          </p>
          <div className="mt-5 relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tags…"
              className="w-full h-11 rounded-xl bg-white/95 text-zinc-800 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
              data-testid="tags-search"
            />
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="py-24 text-center text-zinc-400" data-testid="tags-loading">
            <Loader2 className="animate-spin inline" size={26} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-24 text-center text-zinc-500">No tags match “{q}”.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tags-grid">
            {filtered.map((t) => (
              <Link
                key={t.slug}
                to={`/tags/${t.slug}${dq}`}
                className="group rounded-2xl bg-white border border-zinc-200 hover:border-emerald-300 hover:shadow-md transition-all p-5"
                data-testid={`tags-card-${t.slug}`}
              >
                <div className="font-bold text-zinc-900 text-lg inline-flex items-center gap-2 group-hover:text-emerald-700 transition-colors">
                  <TagIcon size={16} className="text-amber-500 shrink-0" />
                  {t.tag_name}
                </div>
                <div className={`mt-2 text-sm font-medium ${t.product_count > 0 ? "text-emerald-600" : "text-zinc-400"}`}>
                  {t.product_count} {t.product_count === 1 ? "product" : "products"}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
